import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Slide,
} from '@mui/material';
import {
  Close as CloseIcon,
  EmojiEvents as TrophyIcon,
  ListAlt as ListAltIcon,
  Search as SearchIcon,
  ChevronLeft as ChevronLeftIcon,
  Minimize as MinimizeIcon,
} from '@mui/icons-material';
import {
  api,
  CurrentUser,
  SuccessCloserMySummary,
  SuccessCloserMyTransaction,
  SuccessCloserLeaderboardEntry,
} from '../../services/api';
import badgeImg from '../../img/success_closer.png';
import acceleratorImg from '../../img/accelerator.png';
import linusImg from '../../img/linus-low.webp';
import {
  canPromptSelfTestimonial,
  filterLeaderboardByQuery,
} from './successCloserLeaderboard';
import SuperAcceleratorPosterDialog from './SuperAcceleratorPosterDialog';
import { claimSuccessCloserHelp } from './successCloserHelpView';

// ── Confetti + Spinning Coin Overlay ──────────────────────────────────

const CONFETTI_COLORS = ['#ed8936', '#f6e05e', '#48bb78', '#4299e1', '#ed64a6', '#9f7aea', '#fc8181'];

interface ConfettiPiece {
  id: number;
  x: number;
  color: string;
  delay: number;
  duration: number;
  size: number;
  rotation: number;
}

function CelebrationOverlay({ badgeCount, onDone }: { badgeCount: number; onDone: () => void }) {
  const [pieces] = useState<ConfettiPiece[]>(() =>
    Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      delay: Math.random() * 0.8,
      duration: 1.5 + Math.random() * 1.5,
      size: 6 + Math.random() * 8,
      rotation: Math.random() * 360,
    }))
  );

  useEffect(() => {
    const timer = setTimeout(onDone, 4000);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        overflow: 'hidden',
        bgcolor: 'rgba(0, 0, 0, 0.75)',
        animation: 'backdropFadeIn 0.3s ease-out forwards',
        '@keyframes backdropFadeIn': {
          '0%': { backgroundColor: 'rgba(0, 0, 0, 0)' },
          '100%': { backgroundColor: 'rgba(0, 0, 0, 0.75)' },
        },
      }}
    >
      {pieces.map((p) => (
        <Box
          key={p.id}
          sx={{
            position: 'absolute',
            top: -20,
            left: `${p.x}%`,
            width: p.size,
            height: p.size * 0.6,
            bgcolor: p.color,
            borderRadius: '2px',
            animation: `confettiFall ${p.duration}s ease-in ${p.delay}s forwards`,
            transform: `rotate(${p.rotation}deg)`,
            '@keyframes confettiFall': {
              '0%': { top: '-20px', opacity: 1, transform: `rotate(${p.rotation}deg)` },
              '100%': { top: '110vh', opacity: 0, transform: `rotate(${p.rotation + 720}deg)` },
            },
          }}
        />
      ))}
      <Box
        onClick={onDone}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          pointerEvents: 'auto',
          cursor: 'pointer',
          animation: 'centerFadeIn 0.6s ease-out forwards',
          '@keyframes centerFadeIn': {
            '0%': { opacity: 0, transform: 'translate(-50%, -50%) scale(0.8)' },
            '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
          },
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: { xs: 22, sm: 28 }, color: '#fff', textAlign: 'center', textShadow: '0 2px 16px rgba(0,0,0,0.5)', letterSpacing: 1 }}>
          Congratulations!
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: { xs: 14, sm: 16 }, color: 'rgba(255,255,255,0.85)', textAlign: 'center', textShadow: '0 1px 8px rgba(0,0,0,0.4)', mb: 1 }}>
          You have earned yourself as a Super Picker Success Closer
        </Typography>
        <Box
          sx={{
            animation: 'coinSpin 2s ease-out forwards',
            '@keyframes coinSpin': {
              '0%': { transform: 'scale(0.3) rotateY(0deg)', opacity: 0 },
              '30%': { transform: 'scale(1.2) rotateY(540deg)', opacity: 1 },
              '50%': { transform: 'scale(1) rotateY(720deg)', opacity: 1 },
              '100%': { transform: 'scale(1) rotateY(1080deg)', opacity: 1 },
            },
          }}
        >
          <img src={badgeImg} alt="Success Closer Badge" style={{ width: 140, height: 140, filter: 'drop-shadow(0 0 24px rgba(237,137,54,0.6))' }} />
        </Box>
      </Box>
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────────────────

interface SuccessCloserTabProps {
  currentUser: CurrentUser | null;
  autoOpenTransactions?: boolean;
  searchQuery?: string;
  highlightCea?: string | null;
  /** Admins can draw and tag agent areas on the Accelerator poster. */
  isAdmin?: boolean;
}

type SubTab = 'my-transactions' | 'leaderboard';

export default function SuccessCloserTab({ currentUser, autoOpenTransactions, searchQuery, highlightCea, isAdmin = false }: SuccessCloserTabProps) {
  const isMobile = useMediaQuery('(max-width:600px)');

  const [subTab, setSubTab] = useState<SubTab>(autoOpenTransactions ? 'my-transactions' : 'leaderboard');

  const [summary, setSummary] = useState<SuccessCloserMySummary | null>(null);
  const [leaderboard, setLeaderboard] = useState<SuccessCloserLeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Infinite scroll
  const LEADERBOARD_PAGE_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(LEADERBOARD_PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const leaderboardLengthRef = useRef(0);

  // Testimonial dialog
  const [testimonialTx, setTestimonialTx] = useState<SuccessCloserMyTransaction | null>(null);
  const [testimonialText, setTestimonialText] = useState('');
  const [testimonialSaving, setTestimonialSaving] = useState(false);
  const [leaderboardPromptEntry, setLeaderboardPromptEntry] = useState<SuccessCloserLeaderboardEntry | null>(null);
  const [leaderboardPromptText, setLeaderboardPromptText] = useState('');
  const [leaderboardPromptSaving, setLeaderboardPromptSaving] = useState(false);
  const [leaderboardSearch, setLeaderboardSearch] = useState(searchQuery || '');
  type LeaderboardSort = 'latest-closing' | 'most-activity';
  const [leaderboardSort, setLeaderboardSort] = useState<LeaderboardSort>('latest-closing');

  // View testimonial dialog
  const [viewTestimonialEntry, setViewTestimonialEntry] = useState<SuccessCloserLeaderboardEntry | null>(null);
  // Linus's card auto-opens at most once every 48h (tracked server-side); otherwise
  // it waits behind the Help pull tab.
  const [helpDrawerOpen, setHelpDrawerOpen] = useState(false);
  // Asked once per mount — StrictMode's effect re-run reuses the answer, since asking
  // again would come back "already seen".
  const helpClaim = useRef<Promise<boolean> | null>(null);
  useEffect(() => {
    let cancelled = false;
    helpClaim.current ??= claimSuccessCloserHelp();
    void helpClaim.current.then(show => { if (show && !cancelled) setHelpDrawerOpen(true); });
    return () => { cancelled = true; };
  }, []);
  const [helpSpeechOpen, setHelpSpeechOpen] = useState(true);
  const [acceleratorPosterOpen, setAcceleratorPosterOpen] = useState(false);

  // Celebration
  const [showCelebration, setShowCelebration] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, leaderboardRes] = await Promise.all([
        api.getSuccessCloserMySummary(),
        api.getSuccessCloserLeaderboard(),
      ]);
      setSummary(summaryRes);
      setLeaderboard(leaderboardRes.leaderboard);
    } catch (err: any) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Switch to My Transactions tab when redirected from EDM link
  useEffect(() => {
    if (autoOpenTransactions && !loading) {
      setSubTab('my-transactions');
    }
  }, [autoOpenTransactions, loading]);

  // Update search when searchQuery prop changes (from Project Members click)
  useEffect(() => {
    if (searchQuery) {
      setLeaderboardSearch(searchQuery);
      setSubTab('leaderboard');
    }
  }, [searchQuery]);

  // Keep leaderboard length in a ref so the callback ref can always read the latest value
  const filteredLeaderboard = React.useMemo(() => {
    const filtered = filterLeaderboardByQuery(leaderboard, leaderboardSearch);
    if (leaderboardSort === 'most-activity') {
      return [...filtered].sort((a, b) => b.activityCount - a.activityCount);
    }
    // Default: 'latest-closing' - already sorted by backend (testimonial presence then option date)
    return filtered;
  }, [leaderboard, leaderboardSearch, leaderboardSort]);

  useEffect(() => {
    leaderboardLengthRef.current = filteredLeaderboard.length;
  }, [filteredLeaderboard.length]);

  useEffect(() => {
    setVisibleCount(LEADERBOARD_PAGE_SIZE);
  }, [leaderboardSearch]);

  // Callback ref — sets up a fresh observer whenever the sentinel mounts/unmounts
  const sentinelCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => {
            const next = prev + LEADERBOARD_PAGE_SIZE;
            return next < leaderboardLengthRef.current ? next : leaderboardLengthRef.current;
          });
        }
      },
      { threshold: 0.1 },
    );
    observerRef.current.observe(node);
  }, []);

  const handleSaveTestimonial = async () => {
    if (!testimonialTx || !testimonialText.trim()) return;
    setTestimonialSaving(true);
    try {
      const result = await api.saveSuccessCloserTestimonial(testimonialTx.id, testimonialText.trim());
      setTestimonialTx(null);
      setTestimonialText('');
      if (result.isNewBadge) {
        setShowCelebration(true);
      }
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to save testimonial');
    } finally {
      setTestimonialSaving(false);
    }
  };

  const handleSaveLeaderboardPrompt = async () => {
    if (!leaderboardPromptEntry || !leaderboardPromptText.trim()) return;
    setLeaderboardPromptSaving(true);
    try {
      const result = await api.saveSuccessCloserTestimonial(
        leaderboardPromptEntry.transactionId,
        leaderboardPromptText.trim(),
      );
      setLeaderboardPromptEntry(null);
      setLeaderboardPromptText('');
      if (result.isNewBadge) {
        setShowCelebration(true);
      }
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to save testimonial');
    } finally {
      setLeaderboardPromptSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: 'var(--color-accent)' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const transactions = summary?.transactions || [];
  const pendingCount = transactions.filter((t) => t.activityCount > 0 && !t.badgeEarned).length;

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
      {showCelebration && (
        <CelebrationOverlay badgeCount={summary?.badgesEarned || 0} onDone={() => setShowCelebration(false)} />
      )}

      {/* ── Sub-tabs ─────────────────────────────────────────── */}
      <Box
        sx={{
          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.1)',
          mb: 3,
        }}
      >
        <Tabs
          value={subTab}
          onChange={(_, v) => setSubTab(v as SubTab)}
          sx={{
            '& .MuiTab-root': {
              color: 'var(--color-text)',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 15,
              minHeight: 44,
              gap: 0.5,
            },
            '& .Mui-selected': { color: 'var(--color-accent) !important' },
            '& .MuiTabs-indicator': { backgroundColor: 'var(--color-accent)' },
          }}
        >
          <Tab
            value="leaderboard"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <TrophyIcon fontSize="small" />
                Leaderboard
              </Box>
            }
          />
          <Tab
            value="my-transactions"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <ListAltIcon fontSize="small" />
                My Transactions
                {pendingCount > 0 && (
                  <Box
                    sx={{
                      ml: 0.5,
                      px: 0.75,
                      py: 0.1,
                      borderRadius: 10,
                      bgcolor: 'var(--color-accent)',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 800,
                      lineHeight: 1.6,
                      minWidth: 18,
                      textAlign: 'center',
                    }}
                  >
                    {pendingCount}
                  </Box>
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* ══════════════════════════════════════════════════════
          MY TRANSACTIONS TAB
      ══════════════════════════════════════════════════════ */}
      {subTab === 'my-transactions' && (
        <Box>
          {/* Agent Profile Card — single row */}
          <Card
            sx={{
              mb: 3,
              bgcolor: 'var(--color-surface)',
              boxShadow: 'var(--shadow-card)',
              border: '1px solid rgba(var(--color-text-rgb), 0.08)',
            }}
          >
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  flexWrap: 'wrap',
                  rowGap: 1,
                }}
              >
                {/* Avatar */}
                <Avatar
                  src={currentUser?.Photo ? `https://portal.huttonsgroup.com/${currentUser.Photo.startsWith('/') ? currentUser.Photo.substring(1) : currentUser.Photo}` : undefined}
                  sx={{ width: 44, height: 44, bgcolor: 'var(--color-accent)', fontSize: 16, fontWeight: 700, flexShrink: 0 }}
                >
                  {currentUser?.Name ? currentUser.Name.substring(0, 2).toUpperCase() : '??'}
                </Avatar>

                {/* Name + CEA */}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'var(--color-text-strong)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {currentUser?.Name || 'Agent'}
                  </Typography>
                  {currentUser?.CEANumber && (
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', lineHeight: 1 }}>
                      {currentUser.CEANumber}
                    </Typography>
                  )}
                </Box>

                {/* Divider */}
                <Box sx={{ width: '1px', height: 32, bgcolor: 'rgba(var(--color-text-rgb), 0.1)', flexShrink: 0, display: { xs: 'none', sm: 'block' } }} />

                {/* Stat: Total */}
                <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--color-text-strong)', lineHeight: 1 }}>
                    {summary?.totalTransactions || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', whiteSpace: 'nowrap' }}>
                    Total
                  </Typography>
                </Box>

                {/* Stat: Pending */}
                <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: pendingCount > 0 ? 'var(--color-accent)' : 'var(--color-text-strong)', lineHeight: 1 }}>
                    {pendingCount}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', whiteSpace: 'nowrap' }}>
                    Pending
                  </Typography>
                </Box>

                {/* Spacer */}
                <Box sx={{ flex: 1 }} />

                {/* Badge icon — only shown when at least 1 badge earned */}
                {(summary?.badgesEarned || 0) > 0 && (
                  <Box
                    onClick={() => setShowCelebration(true)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                      '&:hover': { opacity: 0.8 },
                    }}
                  >
                    <img src={badgeImg} alt="badge" style={{ width: 36, height: 36 }} />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* CTA banner when no badges and there are pending */}
          {(summary?.badgesEarned || 0) === 0 && pendingCount > 0 && (
            <Box
              sx={{
                mb: 2,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(var(--color-accent-rgb), 0.06)',
                border: '1px dashed rgba(var(--color-accent-rgb), 0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <img src={badgeImg} alt="badge" style={{ width: 28, height: 28, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: 'var(--color-text)' }}>
                Earn your{' '}
                <strong style={{ color: 'var(--color-accent)' }}>Success Closer</strong> badge — add a one-liner testimonial on how Super Picker helped you close the deal.
              </Typography>
            </Box>
          )}

          {/* Transactions Table */}
          <Card
            sx={{
              bgcolor: 'var(--color-surface)',
              border: '1px solid rgba(var(--color-text-rgb), 0.08)',
              boxShadow: 'var(--shadow-card)',
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
                      '& th': {
                        fontWeight: 700,
                        color: 'var(--color-text-strong)',
                        borderBottom: '2px solid rgba(var(--color-text-rgb), 0.12)',
                        whiteSpace: 'nowrap',
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell>Project Name</TableCell>
                    {!isMobile && <TableCell>Address</TableCell>}
                    <TableCell>Unit No</TableCell>
                    <TableCell>Option Date</TableCell>
                    {!isMobile && <TableCell>Role</TableCell>}
                    <TableCell align="center">Activities</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'rgba(var(--color-text-rgb), 0.5)' }}>
                        No transactions found
                      </TableCell>
                    </TableRow>
                  )}
                  {transactions.map((tx) => (
                    <TableRow
                      key={tx.id}
                      sx={{
                        '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.04)' },
                        '& td': {
                          color: 'var(--color-text)',
                          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.06)',
                          py: 1.25,
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong) !important' }}>
                        {tx.projectName}
                      </TableCell>
                      {!isMobile && <TableCell>{tx.address}</TableCell>}
                      <TableCell>{tx.unitNo}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{tx.optionDate}</TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Chip
                            label={tx.transactionRole || '-'}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: 'rgba(var(--color-text-rgb), 0.07)',
                              color: 'var(--color-text)',
                              fontSize: 11,
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Chip
                          label={tx.activityCount}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor: tx.activityCount > 0 ? 'rgba(var(--color-accent-rgb), 0.15)' : 'rgba(var(--color-text-rgb), 0.08)',
                            color: tx.activityCount > 0 ? 'var(--color-accent)' : 'var(--color-text)',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {tx.activityCount > 0 && !tx.badgeEarned ? (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              setTestimonialTx(tx);
                              setTestimonialText(tx.testimonial || '');
                            }}
                            sx={{
                              bgcolor: 'var(--color-accent)',
                              color: '#fff',
                              fontWeight: 600,
                              textTransform: 'none',
                              whiteSpace: 'nowrap',
                              '&:hover': { bgcolor: 'var(--color-accent)', opacity: 0.9 },
                            }}
                          >
                            Add Testimonial
                          </Button>
                        ) : tx.badgeEarned ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setTestimonialTx(tx);
                              setTestimonialText(tx.testimonial || '');
                            }}
                            sx={{
                              borderColor: 'rgba(var(--color-accent-rgb), 0.5)',
                              color: 'var(--color-accent)',
                              textTransform: 'none',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            View
                          </Button>
                        ) : (
                          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.25)' }}>
                            No activities
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Disclaimer footer */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)',
                bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
              }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.4)', fontStyle: 'italic', lineHeight: 1.5 }}>
                * Only transactions completed on or after Super Picker soft launch (1 Mar 2026) are included, and only where there are recorded activity done in Super Picker for the project prior to the transaction date.
              </Typography>
            </Box>
          </Card>
        </Box>
      )}

      {/* ══════════════════════════════════════════════════════
          LEADERBOARD TAB
      ══════════════════════════════════════════════════════ */}
      {subTab === 'leaderboard' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 220 }, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}>
              <InputLabel
                id="leaderboard-sort-label"
                sx={{
                  color: 'rgba(var(--color-text-rgb), 0.7)',
                  '&.Mui-focused': { color: 'var(--color-accent)' },
                  '&.MuiInputLabel-shrink': { bgcolor: 'var(--color-bg)', px: 0.5 },
                }}
              >
                Sort by
              </InputLabel>
              <Select
                labelId="leaderboard-sort-label"
                label="Sort by"
                value={leaderboardSort}
                onChange={(e) => setLeaderboardSort(e.target.value as LeaderboardSort)}
                sx={{
                  color: 'var(--color-text)',
                  fontSize: { xs: 14, sm: 16 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--color-text-rgb), 0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-accent)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-accent)' },
                }}
              >
                <MenuItem value="latest-closing">Rank by latest closing</MenuItem>
                <MenuItem value="most-activity">Rank by most activity</MenuItem>
              </Select>
            </FormControl>
            <Box
              component="button"
              type="button"
              onClick={() => setAcceleratorPosterOpen(true)}
              aria-label="View Super App Accelerator"
              sx={{
                ml: 'auto',
                flexShrink: 0,
                position: 'relative',
                overflow: 'hidden',
                display: 'inline-flex',
                alignItems: 'center',
                gap: { xs: 0.75, sm: 1 },
                height: 40,
                pl: 0.75,
                pr: { xs: 1.5, sm: 2 },
                border: '1px solid rgba(255, 214, 120, 0.55)',
                borderRadius: 999,
                cursor: 'pointer',
                color: '#2a1600',
                fontFamily: 'inherit',
                fontSize: { xs: 11, sm: 14 },
                fontWeight: 800,
                lineHeight: 1.1,
                textAlign: 'left',
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                background: 'linear-gradient(135deg, #ffe08a 0%, #f6b73c 45%, #ed8936 100%)',
                boxShadow: '0 4px 14px rgba(237, 137, 54, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.55)',
                transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-60%',
                  width: '40%',
                  height: '100%',
                  background: 'linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.65), transparent)',
                  transform: 'skewX(-20deg)',
                  animation: 'acceleratorShine 3.2s ease-in-out infinite',
                },
                '@keyframes acceleratorShine': {
                  '0%': { left: '-60%' },
                  '55%, 100%': { left: '130%' },
                },
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 8px 22px rgba(237, 137, 54, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.55)',
                },
                '&:active': { transform: 'translateY(0)' },
                '&:focus-visible': { outline: '2px solid var(--color-accent)', outlineOffset: 2 },
              }}
            >
              <Box
                component="img"
                src={acceleratorImg}
                alt=""
                sx={{
                  width: { xs: 28, sm: 30 },
                  height: { xs: 28, sm: 30 },
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35))',
                }}
              />
              {/* Stacks onto two lines on phones so it fits beside the sort dropdown. */}
              <Box component="span" sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, columnGap: 0.6 }}>
                <span>Super App</span>
                <span>Accelerator</span>
              </Box>
            </Box>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by project name or agent name"
            value={leaderboardSearch}
            onChange={(e) => setLeaderboardSearch(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'var(--color-text)',
                '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.2)' },
                '&:hover fieldset': { borderColor: 'var(--color-accent)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--color-accent)' },
              },
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'rgba(var(--color-text-rgb), 0.5)' }} />,
            }}
          />

          {leaderboard.length === 0 ? (
            <Card sx={{ bgcolor: 'var(--color-surface)', border: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}>
                  No badges earned yet. Be the first!
                </Typography>
              </CardContent>
            </Card>
          ) : filteredLeaderboard.length === 0 ? (
            <Card sx={{ bgcolor: 'var(--color-surface)', border: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}>
                  No matching results.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <>
              <Grid container spacing={2}>
                {filteredLeaderboard.slice(0, visibleCount).map((entry) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={12 / 5}
                    key={entry.transactionId}
                    sx={{ maxWidth: { lg: '20%' }, flexBasis: { lg: '20%' }, minWidth: 220 }}
                  >
                    <Card
                      onClick={() => {
                        // If has testimonial, show view dialog
                        if (entry.hasTestimonial) {
                          setViewTestimonialEntry(entry);
                          return;
                        }
                        // If no testimonial and current user owns it, prompt to add
                        if (canPromptSelfTestimonial(entry, currentUser?.CEANumber)) {
                          setLeaderboardPromptEntry(entry);
                          setLeaderboardPromptText('');
                        }
                      }}
                      sx={{
                        bgcolor: 'var(--color-surface)',
                        border: highlightCea && entry.ceaNumber?.toLowerCase() === highlightCea.toLowerCase()
                          ? '3px solid var(--color-accent)'
                          : entry.hasTestimonial
                            ? '1px solid rgba(var(--color-accent-rgb), 0.35)'
                            : '1px solid rgba(var(--color-text-rgb), 0.08)',
                        boxShadow: entry.hasTestimonial
                          ? '0 2px 12px rgba(var(--color-accent-rgb), 0.12)'
                          : 'var(--shadow-card)',
                        filter: entry.hasTestimonial ? 'none' : 'grayscale(1)',
                        opacity: entry.hasTestimonial ? 1 : 0.55,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        cursor: entry.hasTestimonial || canPromptSelfTestimonial(entry, currentUser?.CEANumber) ? 'pointer' : 'default',
                        ...(highlightCea && entry.ceaNumber?.toLowerCase() === highlightCea.toLowerCase() ? {
                          animation: 'blinkBorder 0.8s ease-in-out 6',
                          '@keyframes blinkBorder': {
                            '0%, 100%': { borderColor: 'var(--color-accent)', boxShadow: '0 0 12px rgba(var(--color-accent-rgb), 0.5)' },
                            '50%': { borderColor: 'transparent', boxShadow: '0 0 0px rgba(var(--color-accent-rgb), 0)' },
                          },
                        } : {}),
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 24px rgba(var(--color-accent-rgb), 0.15)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={entry.photo ? `https://portal.huttonsgroup.com/${entry.photo.startsWith('/') ? entry.photo.substring(1) : entry.photo}` : undefined}
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: 'var(--color-accent)',
                              fontSize: 16,
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {entry.name ? entry.name.substring(0, 2).toUpperCase() : '??'}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.3 }}>
                              {entry.name || 'Agent'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--color-text)', display: 'block', fontWeight: 600, lineHeight: 1.4 }}>
                              {entry.projectName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', lineHeight: 1.3, display: 'block' }}>
                              {entry.ceaNumber}
                            </Typography>
                            {entry.mobile && (
                              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', lineHeight: 1.3, display: 'block', mt: 0.5 }}>
                                {entry.hasTestimonial ? (
                                  <a
                                    href={`tel:${entry.mobile}`}
                                    style={{ color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {entry.mobile}
                                  </a>
                                ) : (
                                  entry.mobile
                                )}
                              </Typography>
                            )}
                            {entry.hasTestimonial && entry.testimonial && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'var(--color-accent)',
                                  lineHeight: 1.3,
                                  display: 'block',
                                  mt: 0.5,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  fontStyle: 'italic',
                                }}
                              >
                                "{entry.testimonial}"
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
                            <Tooltip title="Success Closer" enterTouchDelay={0} leaveTouchDelay={2000} arrow>
                              <img
                                src={badgeImg}
                                alt="Success Closer badge"
                                style={{ width: 34, height: 34, cursor: 'pointer' }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Tooltip>
                            {entry.isAccelerator && (
                              <Tooltip title="Super App Accelerator" enterTouchDelay={0} leaveTouchDelay={2000} arrow>
                                <img
                                  src={acceleratorImg}
                                  alt="Accelerator badge"
                                  style={{ width: 34, height: 34, cursor: 'pointer' }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {visibleCount < filteredLeaderboard.length && (
                <Box ref={sentinelCallbackRef} sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} sx={{ color: 'var(--color-accent)' }} />
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* ── Testimonial Dialog ───────────────────────────────── */}
      <Dialog
        open={!!testimonialTx}
        onClose={() => {
          setTestimonialTx(null);
          setTestimonialText('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-bg)',
            color: 'var(--color-text)',
          },
        }}
      >
        <DialogTitle sx={{ bgcolor: 'var(--color-surface)', borderBottom: '1px solid rgba(var(--color-text-rgb), 0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={badgeImg} alt="badge" style={{ width: 32, height: 32 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
              {testimonialTx?.badgeEarned ? 'Testimonial' : 'Add Testimonial'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography variant="body2" sx={{ mb: 1, color: 'var(--color-text)' }}>
            <strong>{testimonialTx?.projectName}</strong> &mdash; {testimonialTx?.address}, Unit {testimonialTx?.unitNo}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', display: 'block', mb: 2 }}>
            Share how Super Picker helped you close this deal!
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="I used Super Picker to research and compare units, which helped me..."
            value={testimonialText}
            onChange={(e) => setTestimonialText(e.target.value)}
            disabled={testimonialTx?.badgeEarned && !!testimonialTx?.testimonial}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'var(--color-text)',
                '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.2)' },
                '&:hover fieldset': { borderColor: 'var(--color-accent)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--color-accent)' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'var(--color-surface)', borderTop: '1px solid rgba(var(--color-text-rgb), 0.1)' }}>
          <Button
            onClick={() => {
              setTestimonialTx(null);
              setTestimonialText('');
            }}
            sx={{ color: 'var(--color-text)' }}
          >
            Cancel
          </Button>
          {!(testimonialTx?.badgeEarned && !!testimonialTx?.testimonial) && (
            <Button
              onClick={handleSaveTestimonial}
              disabled={testimonialSaving || !testimonialText.trim()}
              variant="contained"
              sx={{
                bgcolor: 'var(--color-accent)',
                color: '#fff',
                fontWeight: 600,
                '&:hover': { bgcolor: 'var(--color-accent)', opacity: 0.9 },
              }}
            >
              {testimonialSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Submit Testimonial'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!leaderboardPromptEntry}
        onClose={() => {
          setLeaderboardPromptEntry(null);
          setLeaderboardPromptText('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-bg)',
            color: 'var(--color-text)',
          },
        }}
      >
        <DialogTitle sx={{ bgcolor: 'var(--color-surface)', borderBottom: '1px solid rgba(var(--color-text-rgb), 0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={badgeImg} alt="badge" style={{ width: 32, height: 32 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
              Add Testimonial
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography variant="body2" sx={{ mb: 1, color: 'var(--color-text)' }}>
            <strong>{leaderboardPromptEntry?.projectName}</strong>
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Entering a one-liner testimonial can boost your leaderboard ranking.
          </Alert>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="I used Super Picker to research and compare units, which helped me..."
            value={leaderboardPromptText}
            onChange={(e) => setLeaderboardPromptText(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'var(--color-text)',
                '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.2)' },
                '&:hover fieldset': { borderColor: 'var(--color-accent)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--color-accent)' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'var(--color-surface)', borderTop: '1px solid rgba(var(--color-text-rgb), 0.1)' }}>
          <Button
            onClick={() => {
              setLeaderboardPromptEntry(null);
              setLeaderboardPromptText('');
            }}
            sx={{ color: 'var(--color-text)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveLeaderboardPrompt}
            disabled={leaderboardPromptSaving || !leaderboardPromptText.trim()}
            variant="contained"
            sx={{
              bgcolor: 'var(--color-accent)',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: 'var(--color-accent)', opacity: 0.9 },
            }}
          >
            {leaderboardPromptSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Submit Testimonial'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── View Testimonial Speech Bubble Dialog ───────────────────────────────── */}
      <Dialog
        open={!!viewTestimonialEntry}
        onClose={() => setViewTestimonialEntry(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-bg)',
            color: 'var(--color-text)',
          },
        }}
      >
        <DialogTitle sx={{ bgcolor: 'var(--color-surface)', borderBottom: '1px solid rgba(var(--color-text-rgb), 0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={badgeImg} alt="Success Closer badge" style={{ width: 32, height: 32 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', ml: 0.5 }}>
              Success Story
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important', pb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* Agent Photo */}
            <Avatar
              src={viewTestimonialEntry?.photo ? `https://portal.huttonsgroup.com/${viewTestimonialEntry.photo.startsWith('/') ? viewTestimonialEntry.photo.substring(1) : viewTestimonialEntry.photo}` : undefined}
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'var(--color-accent)',
                fontSize: 20,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {viewTestimonialEntry?.name ? viewTestimonialEntry.name.substring(0, 2).toUpperCase() : '??'}
            </Avatar>

            {/* Speech Bubble */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Bubble triangle pointer */}
              <Box
                sx={{
                  position: 'relative',
                  ml: 1.5,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -11,
                    top: 20,
                    width: 0,
                    height: 0,
                    borderTop: '10px solid transparent',
                    borderRight: '15px solid var(--color-accent)',
                    borderBottom: '10px solid transparent',
                    zIndex: 0,
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: -9,
                    top: 21,
                    width: 0,
                    height: 0,
                    borderTop: '9px solid transparent',
                    borderRight: '13px solid var(--color-surface)',
                    borderBottom: '9px solid transparent',
                    zIndex: 1,
                  },
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'var(--color-surface)',
                    border: '2px solid var(--color-accent)',
                    boxShadow: '0 0 0 1px rgba(var(--color-accent-rgb), 0.25), 0 6px 20px rgba(var(--color-accent-rgb), 0.25)',
                    borderRadius: 2,
                    p: 2.5,
                    position: 'relative',
                    zIndex: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'var(--color-text)',
                      fontStyle: 'italic',
                      lineHeight: 1.6,
                    }}
                  >
                    <Box component="span" sx={{ fontSize: '1.2em', color: 'var(--color-accent)' }}>"</Box>
                    {viewTestimonialEntry?.testimonial}
                    <Box component="span" sx={{ fontSize: '1.2em', color: 'var(--color-accent)' }}>"</Box>
                  </Typography>
                </Box>
              </Box>

              {/* Agent info below bubble */}
              <Box sx={{ mt: 2, ml: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                    {viewTestimonialEntry?.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', display: 'block' }}>
                    {viewTestimonialEntry?.projectName}
                  </Typography>
                </Box>
                {viewTestimonialEntry?.isAccelerator && (
                  <Box
                    component="img"
                    src={acceleratorImg}
                    alt="Accelerator badge"
                    sx={{
                      height: 56,
                      width: 'auto',
                      flexShrink: 0,
                      filter: 'drop-shadow(0 0 10px rgba(var(--color-accent-rgb), 0.45))',
                      animation: 'acceleratorSpin 1.4s ease-out',
                      '@keyframes acceleratorSpin': {
                        '0%': { transform: 'rotateY(0deg) scale(0.6)', opacity: 0 },
                        '40%': { transform: 'rotateY(360deg) scale(1.15)', opacity: 1 },
                        '100%': { transform: 'rotateY(720deg) scale(1)', opacity: 1 },
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'var(--color-surface)', borderTop: '1px solid rgba(var(--color-text-rgb), 0.1)' }}>
          <Button
            onClick={() => setViewTestimonialEntry(null)}
            sx={{ color: 'var(--color-text)' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Super Accelerator poster ───────────────────────────────── */}
      <SuperAcceleratorPosterDialog
        open={acceleratorPosterOpen}
        onClose={() => setAcceleratorPosterOpen(false)}
        isAdmin={isAdmin}
      />

      {/* ── Transformation Office: right-edge pull tab ───────────────── */}
      {!helpDrawerOpen && (
        <Box
          onClick={() => setHelpDrawerOpen(true)}
          role="button"
          aria-label="Open help message"
          sx={{
            position: 'fixed',
            right: 0,
            bottom: { xs: 80, sm: 120 },
            zIndex: 1299,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.25,
            py: 1.5,
            bgcolor: 'var(--color-accent)',
            color: '#fff',
            borderTopLeftRadius: 10,
            borderBottomLeftRadius: 10,
            cursor: 'pointer',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.35)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateX(-3px)',
              boxShadow: '0 8px 22px rgba(0, 0, 0, 0.45)',
            },
            animation: 'helpTabPulse 2.4s ease-in-out infinite',
            '@keyframes helpTabPulse': {
              '0%, 100%': { transform: 'translateX(0)' },
              '50%': { transform: 'translateX(-4px)' },
            },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 24 }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              letterSpacing: 1,
              textTransform: 'uppercase',
              lineHeight: 1,
            }}
          >
            Help
          </Typography>
        </Box>
      )}

      {/* ── Transformation Office Floating Help Card ───────────────── */}
      <Slide direction="left" in={helpDrawerOpen} mountOnEnter unmountOnExit timeout={350}>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            right: { xs: 8, sm: 24 },
            zIndex: 1300,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 0,
            pointerEvents: 'auto',
          }}
        >
          {/* Speech bubble */}
          {helpSpeechOpen && (
          <Box
            sx={{
              position: 'relative',
              mb: { xs: '220px', sm: '320px' },
              mr: { xs: '-12px', sm: '-14px' },
              width: { xs: 240, sm: 340 },
              px: 2.5,
              py: 2,
              bgcolor: 'var(--color-surface)',
              border: '1px solid rgba(var(--color-accent-rgb), 0.4)',
              borderRadius: 3,
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(var(--color-accent-rgb), 0.12)',
              '&::after': {
                content: '""',
                position: 'absolute',
                right: -8,
                bottom: 18,
                width: 16,
                height: 16,
                bgcolor: 'var(--color-surface)',
                borderTop: '1px solid rgba(var(--color-accent-rgb), 0.4)',
                borderRight: '1px solid rgba(var(--color-accent-rgb), 0.4)',
                transform: 'rotate(45deg)',
              },
            }}
          >
            <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.25 }}>
              <IconButton
                onClick={() => setHelpSpeechOpen(false)}
                size="small"
                aria-label="Minimize message"
                title="Minimize"
                sx={{
                  color: 'rgba(var(--color-text-rgb), 0.6)',
                  '&:hover': { color: 'var(--color-text-strong)' },
                }}
              >
                <MinimizeIcon fontSize="small" sx={{ mt: '-8px' }} />
              </IconButton>
              <IconButton
                onClick={() => setHelpDrawerOpen(false)}
                size="small"
                aria-label="Close message"
                title="Close"
                sx={{
                  color: 'rgba(var(--color-text-rgb), 0.6)',
                  '&:hover': { color: 'var(--color-text-strong)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: 'var(--color-accent)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                display: 'block',
                mb: 0.5,
              }}
            >
              Transformation Office
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--color-text)', lineHeight: 1.7, pr: 2 }}>
              Want your card to{' '}
              <Box component="span" sx={{ color: 'var(--color-accent)', fontWeight: 700 }}>
                shine on the leaderboard
              </Box>
              ? Head to{' '}
              <Box component="span" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                My Transactions
              </Box>{' '}
              and share your story — tell us how Super Picker helped you close the deal.
              <Box component="span" sx={{ display: 'block', mt: 1 }}>
                Need a hand? Reach out to{' '}
                <Box component="span" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                  Linus
                </Box>{' '}
                at{' '}
                <Box
                  component="a"
                  href="tel:82253878"
                  sx={{
                    color: 'var(--color-accent)',
                    fontWeight: 700,
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  8225&nbsp;3878
                </Box>
                .
              </Box>
            </Typography>
          </Box>
          )}

          {/* Linus image — full body, popping out from bottom-right */}
          <Box
            component="img"
            src={linusImg}
            alt="Linus Low"
            onClick={helpSpeechOpen ? undefined : () => setHelpSpeechOpen(true)}
            title={helpSpeechOpen ? undefined : 'Show message'}
            sx={{
              flexShrink: 0,
              width: { xs: 140, sm: 200 },
              height: 'auto',
              display: 'block',
              filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.4))',
              userSelect: 'none',
              pointerEvents: helpSpeechOpen ? 'none' : 'auto',
              cursor: helpSpeechOpen ? 'default' : 'pointer',
            }}
          />
        </Box>
      </Slide>
    </Box>
  );
}

// ── Small helper component ────────────────────────────────────────────

function StatBox({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 1, sm: 1.5 },
        borderRadius: 2,
        bgcolor: accent ? 'rgba(var(--color-accent-rgb), 0.1)' : 'rgba(var(--color-text-rgb), 0.04)',
        border: `1px solid ${accent ? 'rgba(var(--color-accent-rgb), 0.3)' : 'rgba(var(--color-text-rgb), 0.08)'}`,
        textAlign: 'center',
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 800, color: accent ? 'var(--color-accent)' : 'var(--color-text-strong)', lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'var(--color-text)', mt: 0.5, display: 'block', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  );
}
